import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Select from 'react-select';
import configService from '../services/configService';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import openIDConnectService from '../services/openIDConnectService';
import authService from '../services/authService';
import { Buffer } from 'buffer';
import { Detector } from 'react-detect-offline';
import { getPollingConfig } from '../helpers/utils';
import { Translate, CaretDown, Check } from '@phosphor-icons/react';

export default function NavHeader({ langOptions, i18nKeyPrefix = 'header' }) {
  const { i18n, t } = useTranslation('translation', {
    keyPrefix: i18nKeyPrefix,
  });
  const [selectedLang, setSelectedLang] = useState();
  const [config, setConfig] = useState({});
  const [langMenuOpen, setLangMenuOpen] = useState(false);
  const langTriggerRef = useRef(null);
  const langMenuRef = useRef(null);
  const authServices = new authService(openIDConnectService);
  const authorizeQueryParam = 'authorize_query_param';
  const ui_locales = 'ui_locales';
  const pollingConfig = getPollingConfig();
  // Decode the authorize query param
  const decodedBase64 = Buffer.from(
    authServices.getAuthorizeQueryParam(),
    'base64'
  ).toString();

  var urlSearchParams = new URLSearchParams(decodedBase64);

  const changeLanguageHandler = (e) => {
    setLangMenuOpen(false);
    i18n.changeLanguage(e.value);
  };

  // Radix only sees pointer events on this document; clicks inside iframes
  // (captcha, wallet QR) or a scroll would otherwise leave the menu open.
  useEffect(() => {
    if (!langMenuOpen) return undefined;
    const close = () => setLangMenuOpen(false);
    const onPointerDown = (event) => {
      const target = event.target;
      if (
        langTriggerRef.current?.contains(target) ||
        langMenuRef.current?.contains(target)
      ) {
        return;
      }
      close();
    };
    document.addEventListener('pointerdown', onPointerDown, true);
    window.addEventListener('blur', close);
    window.addEventListener('resize', close);
    window.addEventListener('scroll', close, true);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown, true);
      window.removeEventListener('blur', close);
      window.removeEventListener('resize', close);
      window.removeEventListener('scroll', close, true);
    };
  }, [langMenuOpen]);

  const customStyles = {
    control: (base) => ({
      ...base,
      border: 0,
    }),
    ...(config['remove_language_indicator_pipe'] && {
      valueContainer: (base) => ({
        ...base,
        padding: 0,
      }),
      indicatorSeparator: (base) => ({
        ...base,
        display: 'none',
      }),
      dropdownIndicator: (base) => ({
        ...base,
        'padding-left': 0,
        color: '#140701',
      }),
      menu: (base) => ({
        ...base,
        'min-width': '100px',
      }),
    }),
  };

  // ✅ Fetch config asynchronously
  useEffect(() => {
    const fetchConfig = async () => {
      const cfg = await configService();
      setConfig(cfg || {});
    };
    fetchConfig();
  }, []);

  useEffect(() => {
    //Gets fired when changeLanguage got called.
    i18n.on('languageChanged', function (lng) {
      let language = langOptions.find((option) => {
        return option.value === lng;
      });

      if (language) {
        setSelectedLang(language);
      }
      // Setting up the current i18n language in the URL on every language change.

      // Convert the decoded string to JSON
      var jsonObject = {};
      urlSearchParams.forEach(function (value, key) {
        jsonObject[key] = value;

        // Assign the current i18n language to the ui_locales
        if (key === ui_locales) {
          jsonObject[key] = language.value;
        }
      });

      // Convert the JSON back to decoded string
      Object.entries(jsonObject).forEach(([key, value]) => {
        urlSearchParams.set(key, value);
      });

      // Encode the string
      var encodedString = urlSearchParams.toString();

      const encodedBase64 = Buffer.from(encodedString).toString('base64');

      // Remove the old authorizeQueryParam from the local storage
      localStorage.removeItem(authorizeQueryParam);

      // Insert the new authorizeQueryParam to the local storage
      localStorage.setItem(authorizeQueryParam, encodedBase64);
    });

    if (!langOptions || langOptions.length === 0) {
      return;
    }

    let lang = langOptions.find((option) => {
      return option.value === i18n.language;
    });

    if (!lang) {
      const defaultLanguageCode = window._env_.DEFAULT_LANG;

      // Find the language option that matches the extracted language code
      const defaultLang = langOptions.find(
        (option) => option.value === defaultLanguageCode
      );
      setSelectedLang(defaultLang);
    } else {
      setSelectedLang(lang);
    }
  }, [langOptions]);

  var dropdownItemClass =
    'group text-[14px] leading-none flex items-center relative select-none outline-none data-[disabled]:pointer-events-none cursor-pointer py-3 langDropdown';

  var borderBottomClass = 'border-b-[1px]';

  const languageMeta = {
    en: { code: 'ENG', label: 'English' },
    am: { code: 'አማ', label: 'አማርኛ (Amharic)' },
  };

  const getLanguageMeta = (language) =>
    languageMeta[language.value] || {
      code: language.value.toUpperCase(),
      label: language.label,
    };

  const languageMenuHeading = i18n.language.toLowerCase().startsWith('am')
    ? 'ቋንቋ'
    : t('language', { defaultValue: 'Language' });

  return (
    <nav
      className="bg-white border-gray-500 md:px-[4rem] py-2 px-[0.5rem] navbar-header"
      id="navbar-header"
    >
      <div className="flex h-full items-center justify-between">
        <div className="ltr:sm:ml-8 rtl:sm:mr-8 ltr:ml-1 rtl:mr-1">
          <img className="brand-logo" alt="brand_logo" />
        </div>
        <Detector
          polling={{
            // AppRouter owns /health polling — disable here to avoid duplicate storms.
            enabled: false,
            url: pollingConfig.url,
            interval: pollingConfig.interval,
            timeout: pollingConfig.timeout,
          }}
          render={({ online }) =>
            online && (
              <div
                className="flex rtl:sm:ml-8 ltr:sm:mr-8 rtl:ml-1 ltr:mr-1"
                id="language_dropdown"
              >
                {config['outline_dropdown'] ? (
                  <Select
                    styles={customStyles}
                    isSearchable={false}
                    className="appearance-none"
                    value={selectedLang}
                    options={langOptions}
                    onChange={changeLanguageHandler}
                    id="language_selection"
                  />
                ) : (
                  <DropdownMenu.Root
                    open={langMenuOpen}
                    onOpenChange={setLangMenuOpen}
                    modal={false}
                  >
                    <DropdownMenu.Trigger asChild>
                      <span
                        className="vf-lang-pill inline-flex items-center justify-center gap-2 outline-none hover:cursor-pointer text-white select-none"
                        aria-label="Customise options"
                        id="language_selection"
                        ref={langTriggerRef}
                      >
                        <Translate
                          size={16}
                          weight="bold"
                          className="vf-lang-glyph"
                          aria-hidden="true"
                        />
                        <span className="vf-lang-code font-semibold text-xs tracking-wider">
                          {selectedLang
                            ? getLanguageMeta(selectedLang).code
                            : 'ENG'}
                        </span>
                        <CaretDown
                          size={12}
                          weight="bold"
                          className="vf-lang-chevron"
                          aria-hidden="true"
                        />
                      </span>
                    </DropdownMenu.Trigger>
                    <DropdownMenu.Portal>
                      <DropdownMenu.Content
                        className="vf-lang-menu min-w-[220px] bg-[#0F4356] rounded-md shadow-md will-change-[opacity,transform] data-[side=top]:animate-slideDownAndFade data-[side=right]:animate-slideLeftAndFade data-[side=bottom]:animate-slideUpAndFade data-[side=left]:animate-slideRightAndFade px-3 py-2 border border-[#BCBCBC] outline-0 relative top-[-0.5rem] text-white"
                        sideOffset={5}
                        ref={langMenuRef}
                      >
                        <div className="vf-lang-menu-heading">
                          {languageMenuHeading}
                        </div>
                        {langOptions.map((key, idx) => {
                          const language = getLanguageMeta(key);

                          return (
                            <DropdownMenu.Item
                              id={key.value + idx}
                              key={key.value}
                              className={
                                i18n.language === key.value
                                  ? langOptions.length - 1 !== idx
                                    ? `selectedLang vf-lang-menu-item ${dropdownItemClass} ${borderBottomClass}`
                                    : `selectedLang vf-lang-menu-item ${dropdownItemClass}`
                                  : langOptions.length - 1 !== idx
                                    ? `vf-lang-menu-item ${dropdownItemClass} ${borderBottomClass}`
                                    : `vf-lang-menu-item ${dropdownItemClass}`
                              }
                              onSelect={() => changeLanguageHandler(key)}
                            >
                              <span className="vf-lang-menu-code">
                                {language.code}
                              </span>
                              <span className="vf-lang-menu-label">
                                {language.label}
                              </span>
                              <div className="ml-auto">
                                {i18n.language === key.value && (
                                  <Check
                                    size={16}
                                    weight="bold"
                                    className="checkIcon relative top-[1px]"
                                    aria-hidden="true"
                                  />
                                )}
                              </div>
                            </DropdownMenu.Item>
                          );
                        })}
                        <DropdownMenu.Arrow asChild>
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 30 10"
                            stroke="#BCBCBC"
                            height={7}
                          >
                            <polygon points="0,0 30,0 15,10" fill="#fff" />
                            <line
                              x1="1"
                              y1="0"
                              x2="29"
                              y2="0"
                              stroke="#fff"
                              strokeWidth="1"
                            />
                          </svg>
                        </DropdownMenu.Arrow>
                      </DropdownMenu.Content>
                    </DropdownMenu.Portal>
                  </DropdownMenu.Root>
                )}
              </div>
            )
          }
        />
      </div>
    </nav>
  );
}
